import { useMemo } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { StiffeningPlanSegmentAnchorLabelsDTO } from '@api/fastAPI.schemas'
import { useGetStiffeningPlanAnchorLabels } from '@api/stiffening-plan-anchor-labels/stiffening-plan-anchor-labels'
import { useSnackbar } from '@ui/snackbar'
import { usePersistentParams } from 'src/hooks'

/**
 * The Zuganker and Schubanker labels the stiffening plan export prints, per stiffening segment
 * guid, for the stiffening plan preview. Refetched on every enter, since the anchors follow the
 * results, which are computed elsewhere.
 */
const useStiffeningPlanAnchorLabels = (
  enabled: boolean,
): Record<string, StiffeningPlanSegmentAnchorLabelsDTO> => {
  const { projectId } = usePersistentParams() as { projectId?: string }
  const { enqueueSnackbar } = useSnackbar()

  const { data } = useGetStiffeningPlanAnchorLabels<
    StiffeningPlanSegmentAnchorLabelsDTO[],
    AxiosError
  >(projectId as string, {
    query: {
      enabled: enabled && !!projectId,
      onError: error =>
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Zug- und Schubanker'), {
          variant: 'error',
        }),
      // Overrides the client defaults (`staleTime: Infinity`, `refetchOnMount: false`), so
      // entering the stiffening plan preview refetches.
      staleTime: 0,
      refetchOnMount: 'always',
    },
  })

  return useMemo(
    () =>
      Object.fromEntries((enabled ? data ?? [] : []).map(labels => [labels.segment_guid, labels])),
    [enabled, data],
  )
}

export default useStiffeningPlanAnchorLabels
